import type { PurchasedProduct } from "@/hooks/usePurchasedProducts";
import type { ShippingAddress } from "@/hooks/usePurchasedProductDetail";
import { groupSubscriptions } from "shared/subscriptions";
import { discountPillPercent } from "./formatters";
import { isActiveSubscription } from "./subscriptionEligibility";

/** One active subscription eligible to participate in a merge. */
export interface MergeableSubscription {
  subscriptionId: number;
  /** All line items on this subscription, in original order. */
  products: PurchasedProduct[];
  /** Normalized billing interval unit (e.g. "week"). */
  intervalUnit: string;
  intervalCount: number;
  nextBillingDate: string | null;
  shippingAddress: ShippingAddress | null;
}

/**
 * Strict frequency bucket key. Two subscriptions can merge only when BOTH the
 * interval unit AND the interval count match (e.g. "every 2 weeks" ≠ "every 1
 * week" ≠ "every 1 month"). Returns null when either part is missing.
 */
export function frequencyKey(
  intervalUnit: string | null | undefined,
  intervalCount: number | null | undefined,
): string | null {
  if (!intervalUnit || intervalCount == null || !Number.isFinite(intervalCount)) {
    return null;
  }
  return `${intervalUnit.trim().toLowerCase()}:${intervalCount}`;
}

/** Human-readable frequency, e.g. "every 2 weeks" / "every week". */
export function frequencyLabel(unit: string, count: number): string {
  const u = unit.trim().toLowerCase();
  return count === 1 ? `every ${u}` : `every ${count} ${u}s`;
}

/**
 * Fold the customer's purchased products into the active subscriptions that
 * could take part in a merge — one entry per subscription that is active, has
 * a readable frequency, and has a shipping address on record. Cancelled/paused
 * subscriptions and non-subscription rows are excluded; so are subscriptions
 * with no address — a destination we can't read is a data gap on our side, not
 * something to invite the customer to merge.
 */
export function getMergeableSubscriptions(
  products: PurchasedProduct[] | null | undefined,
): MergeableSubscription[] {
  if (!products?.length) return [];
  const out: MergeableSubscription[] = [];
  for (const group of groupSubscriptions(products)) {
    if (group.subscriptionId == null) continue;
    const rep = group.products[0];
    if (!rep || !isActiveSubscription(rep)) continue;
    if (group.products.some((p) => p.subscription_cancel_scheduled_at)) continue;
    const unit = rep.subscription_billing_interval;
    const count = rep.subscription_billing_interval_count;
    if (frequencyKey(unit, count) == null) continue;
    // Contract-level like the shipping price: read the first line item that
    // carries an address rather than assuming the first row does.
    const shippingAddress =
      group.products.find((p) => p.subscription_shipping_address)
        ?.subscription_shipping_address ?? null;
    if (!shippingAddress) continue;
    out.push({
      subscriptionId: group.subscriptionId,
      products: group.products,
      intervalUnit: (unit as string).trim().toLowerCase(),
      intervalCount: count as number,
      nextBillingDate: rep.subscription_next_billing_date ?? null,
      shippingAddress,
    });
  }
  return out;
}

/** Normalize one address field for comparison: trim, lowercase, collapse spaces. */
function normalizeAddressField(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Destination identity for merge bucketing, compared field-by-field so cosmetic
 * record differences never split a bucket: the recipient name is irrelevant to
 * where a box lands, and province is omitted because records carry it
 * inconsistently ("CA" vs "California") while city+zip+country already pin the
 * region. Street, unit, city, zip, and country all participate — "Apt 4B" vs
 * "Apt 12" is a real destination difference. Null when the address is missing.
 */
export function addressBucketKey(
  address: ShippingAddress | null,
): string | null {
  if (!address) return null;
  return [
    address.address1,
    address.address2,
    address.city,
    address.zip,
    address.country,
  ]
    .map(normalizeAddressField)
    .join("|");
}

/**
 * Promo / code discount identity for merge bucketing — not Subscribe & Save.
 *
 * Compare `subscription_discounted_price` against the recurring
 * `subscription_unit_price` (fallback: retail `price`). Using one-time/retail
 * as the baseline would treat ordinary S&S savings as a "discount" and split
 * otherwise-identical subscriptions into different buckets.
 *
 * Subscriptions merge only when every line shares the same story: either no
 * active promo (`none`) or the same rounded percentage. Mixed promos within
 * one subscription are unreadable for merge purposes (null).
 */
export function subscriptionDiscountBucketKey(
  sub: MergeableSubscription,
): string | null {
  const seen = new Set<string>();
  for (const p of sub.products) {
    const unit = finiteUnitPrice(p.subscription_unit_price) ?? finiteUnitPrice(p.price);
    const net =
      finiteUnitPrice(p.subscription_discounted_price) ?? unit;
    const pct = discountPillPercent(
      p.subscription_discounts,
      unit,
      net,
    );
    seen.add(pct == null ? "none" : `p:${pct}`);
  }
  if (seen.size === 0) return "none";
  if (seen.size > 1) return null;
  return [...seen][0]!;
}

/**
 * Recurring unit-price identity for merge bucketing. Two subscriptions can
 * combine only when every line on each contract shares the same
 * `subscription_unit_price` (to the cent). Mixed unit prices within one
 * subscription, or a missing readable unit price, are unreadable (null).
 */
export function subscriptionUnitPriceBucketKey(
  sub: MergeableSubscription,
): string | null {
  const seen = new Set<string>();
  for (const p of sub.products) {
    const unit = finiteUnitPrice(p.subscription_unit_price) ?? finiteUnitPrice(p.price);
    if (unit == null) return null;
    seen.add(`u:${Math.round(unit * 100)}`);
  }
  if (seen.size === 0) return null;
  if (seen.size > 1) return null;
  return [...seen][0]!;
}

function finiteUnitPrice(
  v: string | number | null | undefined,
): number | null {
  if (v == null) return null;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

/**
 * Combined merge-bucket key: two subscriptions can merge only when they share
 * billing frequency, shipping destination, promo discount, and recurring unit
 * price. Returns null when any part is unreadable — that subscription is
 * excluded from merge prompting.
 */
export function mergeBucketKey(sub: MergeableSubscription): string | null {
  const fk = frequencyKey(sub.intervalUnit, sub.intervalCount);
  const ak = addressBucketKey(sub.shippingAddress);
  const dk = subscriptionDiscountBucketKey(sub);
  const uk = subscriptionUnitPriceBucketKey(sub);
  if (fk == null || ak == null || dk == null || uk == null) return null;
  return `${fk}|${ak}|${dk}|${uk}`;
}

/**
 * True when at least one merge bucket (same frequency, shipping address,
 * discount, and unit price) holds ≥2 active subscriptions — i.e. the customer
 * actually has something they could merge. Drives the banner.
 */
export function hasMergeableSubscriptions(
  products: PurchasedProduct[] | null | undefined,
): boolean {
  const counts = new Map<string, number>();
  for (const sub of getMergeableSubscriptions(products)) {
    const key = mergeBucketKey(sub);
    if (key == null) continue;
    const next = (counts.get(key) ?? 0) + 1;
    if (next >= 2) return true;
    counts.set(key, next);
  }
  return false;
}

/** One chip representing a mergeable subscription in the banner. */
export interface MergeChip {
  subscriptionId: number;
  title: string;
  imageUrl: string | null;
  /** Raw next billing date; format for display at the render site. */
  nextBillingDate: string | null;
}

/**
 * The single largest set of the GIVEN subscriptions that can actually merge
 * together — the merge bucket (same frequency, address, discount, and unit
 * price) with the most members (≥2). When more than one bucket qualifies
 * (e.g. three biweekly to one address + two monthly, or same-frequency subs
 * split across addresses, discounts, or unit prices), only the largest is
 * returned so callers act on one real, combinable group. Ties are broken by
 * first appearance; returns [] when no bucket has ≥2 members.
 */
export function largestMergeBucket(
  subs: MergeableSubscription[],
): MergeableSubscription[] {
  const buckets = new Map<string, MergeableSubscription[]>();
  for (const sub of subs) {
    const key = mergeBucketKey(sub);
    if (key == null) continue;
    const list = buckets.get(key);
    if (list) list.push(sub);
    else buckets.set(key, [sub]);
  }
  let best: MergeableSubscription[] = [];
  for (const list of buckets.values()) {
    if (list.length >= 2 && list.length > best.length) best = list;
  }
  return best;
}

/** {@link largestMergeBucket} over the customer's raw purchased products. */
export function getLargestMergeableBucket(
  products: PurchasedProduct[] | null | undefined,
): MergeableSubscription[] {
  return largestMergeBucket(getMergeableSubscriptions(products));
}

/**
 * Chips for the largest mergeable group — one per subscription, carrying the
 * subscription's representative product name, image, and next billing date.
 * Each subscription contributes its first line item that has an image (falling
 * back to the first line item), so every chip shows a distinct product.
 */
export function getMergeableSubscriptionChips(
  products: PurchasedProduct[] | null | undefined,
): MergeChip[] {
  const out: MergeChip[] = [];
  for (const sub of getLargestMergeableBucket(products)) {
    const rep = sub.products.find((p) => p.image_url) ?? sub.products[0];
    if (!rep) continue;
    out.push({
      subscriptionId: sub.subscriptionId,
      title: rep.title,
      imageUrl: rep.image_url ?? null,
      nextBillingDate: sub.nextBillingDate,
    });
  }
  return out;
}

/**
 * Real per-billing shipping fee of a subscription, from Shopify's
 * `SubscriptionContract.deliveryPrice` (forwarded as `subscription_shipping_price`).
 * The value is contract-level, so every line item of the subscription carries the
 * same number — we read the first defined one rather than summing across items.
 * Returns null when the backend hasn't populated a shipping price for this sub.
 */
export function subscriptionShippingCost(
  sub: MergeableSubscription,
): number | null {
  for (const p of sub.products) {
    const cost = p.subscription_shipping_price;
    if (cost != null && Number.isFinite(cost)) return cost;
  }
  return null;
}

/** True when a subscription currently pays a real (non-zero) shipping fee. */
function hasShippingCost(sub: MergeableSubscription): boolean {
  const cost = subscriptionShippingCost(sub);
  return cost != null && cost > 0;
}

/**
 * True when merging the largest mergeable group could save on shipping — i.e. at
 * least one subscription in it currently pays a real shipping fee. Scoped to the
 * same group the banner shows so the chips and the copy always agree. Gates the
 * merged-shipping quote and drives the qualitative fallback copy; the exact dollar
 * saving comes from the backend `merge-preview` quote, not from this.
 */
export function mergeSavesShipping(
  products: PurchasedProduct[] | null | undefined,
): boolean {
  return getLargestMergeableBucket(products).some(hasShippingCost);
}

/**
 * Total shipping the given subscriptions pay today (sum of their real per-billing
 * fees). The saving from merging is this total minus the single merged-order fee
 * returned by the backend `merge-preview` quote.
 */
export function currentShippingCostTotal(
  subs: MergeableSubscription[],
): number {
  let total = 0;
  for (const sub of subs) {
    const cost = subscriptionShippingCost(sub);
    if (cost != null && cost > 0) total += cost;
  }
  return total;
}

/** Format a shipping address as a one-line summary (for notes / email). */
export function formatShippingAddressOneLine(
  address: ShippingAddress | null,
): string | null {
  if (!address) return null;
  const parts = [
    [address.first_name, address.last_name].filter(Boolean).join(" ").trim(),
    address.address1,
    address.address2,
    address.city,
    [address.province_code || address.province, address.zip]
      .filter(Boolean)
      .join(" ")
      .trim(),
    address.country,
  ]
    .map((p) => (p ?? "").trim())
    .filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}

/**
 * True when the given subscriptions ship to more than one distinct destination
 * (field-by-field, so recipient-name or province-spelling variance doesn't
 * count). An unreadable address counts as differing — we can't vouch that it
 * matches, so the merge request gets flagged for confirmation.
 */
export function hasDifferingShippingAddresses(
  subs: MergeableSubscription[],
): boolean {
  const seen = new Set<string>();
  for (const sub of subs) {
    const key = addressBucketKey(sub.shippingAddress);
    if (key == null) return true;
    seen.add(key);
    if (seen.size > 1) return true;
  }
  return false;
}
